import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../context/AdminAuthContext';
import { 
  ShieldCheck, Lock, CheckCircle, ChevronDown, ChevronRight, 
  User, Search, Save, KeyRound, Loader2 
} from 'lucide-react';

const GiveAccess = () => {
  const { token, role, permissions } = useAdminAuth(); 
  const [subordinates, setSubordinates] = useState([]);
  const [availableMenus, setAvailableMenus] = useState<any[]>([]); 
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [perms, setPerms] = useState<any>({}); 
  const [loading, setLoading] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // 1. Parse Admin Permissions for Security Check
  const myPermissions = typeof permissions === 'string' ? JSON.parse(permissions || "{}") : (permissions || {});

  useEffect(() => {
    // Fetch Subordinates
    fetch("/springboot/api/admin/team/my-subordinates", {
      headers: { "Authorization": `Bearer ${token}` }
    })
    .then(res => res.json())
    .then(data => setSubordinates(data))
    .catch(err => console.error("Error fetching subordinates:", err));

    // Fetch All Sidebar Menus
    fetch("/nodejs/api/sidebar/all")
      .then(res => res.json())
      .then(data => setAvailableMenus(Array.isArray(data) ? data : []))
      .catch(err => console.error("Error fetching menus:", err));
  }, [token]);

  const handleUserSelect = (user: any) => {
    setSelectedUser(user);
    try {
      setPerms(JSON.parse(user.permissions || "{}"));
    } catch (e) {
      setPerms({});
    }
  };

  // Logic: Auto-check Parent if Child is checked
  const handleSubItemToggle = (parentTitle: string, path: string) => {
    setPerms((prev: any) => {
      const isChecking = !prev[path];
      const newPerms = { ...prev, [path]: isChecking };

      if (isChecking) {
        newPerms[parentTitle] = true; 
      }
      return newPerms;
    });
  };

  const saveAccess = async () => {
    if (!selectedUser) return;
    setLoading(true);
    try {
      const res = await fetch("/springboot/api/admin/auth/update-permissions", {
        method: "POST",
        headers: { 
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json" 
        },
        body: JSON.stringify({
          userId: selectedUser.id,
          permissions: JSON.stringify(perms)
        })
      }); 

      if (res.ok) {
        alert("Permissions Updated Successfully!");
      } else {
        alert("Failed to save permissions.");
      }
    } catch (e) {
      alert("Server Error");
    } finally {
      setLoading(false);
    }
  };

  // Filter Users
  const filteredUsers = subordinates.filter((u: any) => 
    u.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white font-sans animate-fade-in-up">
      <div className="max-w-7xl mx-auto px-6 py-12">
        
        {/* Header */}
        <div className="mb-12 border-b border-gray-100 pb-6 flex items-center gap-5">
          <div className="w-14 h-14 bg-[#006d5b]/10 rounded-2xl flex items-center justify-center text-[#006d5b] shadow-sm">
             <KeyRound size={28} />
          </div>
          <div>
            <h1 className="text-3xl font-black text-gray-900 tracking-tight">Access Control</h1>
            <p className="text-gray-500 font-medium mt-1 text-sm">Configure module visibility and user privileges.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 h-[calc(100vh-250px)] min-h-[600px]">
          
          {/* LEFT: User Directory (4 Columns) */}
          <div className="lg:col-span-4 flex flex-col border border-gray-200 rounded-[30px] overflow-hidden bg-white shadow-sm">
            
            {/* Search Bar */}
            <div className="p-5 border-b border-gray-100 bg-gray-50/50">
               <div className="relative">
                 <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                 <input 
                   type="text" 
                   placeholder="Find team member..." 
                   className="w-full bg-white border border-gray-200 rounded-xl py-3 pl-11 pr-4 text-sm font-bold focus:border-[#006d5b] outline-none transition-all placeholder:text-gray-400"
                   onChange={(e) => setSearchTerm(e.target.value)}
                 />
               </div>
            </div>

            {/* User List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {filteredUsers.length > 0 ? (
                filteredUsers.map((sub: any) => (
                  <div 
                    key={sub.id} 
                    onClick={() => handleUserSelect(sub)}
                    className={`p-4 rounded-2xl cursor-pointer transition-all border flex items-center gap-4 ${
                      selectedUser?.id === sub.id 
                        ? 'bg-[#006d5b] border-[#006d5b] shadow-lg shadow-[#006d5b]/20 text-white' 
                        : 'bg-white border-gray-100 hover:border-[#006d5b]/30 hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black uppercase text-sm ${selectedUser?.id === sub.id ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
                        {sub.name.charAt(0)}
                    </div>
                    <div>
                      <p className={`font-bold text-sm ${selectedUser?.id === sub.id ? 'text-white' : 'text-gray-900'}`}>{sub.name}</p>
                      <p className={`text-[10px] font-black uppercase tracking-widest mt-0.5 ${selectedUser?.id === sub.id ? 'text-white/70' : 'text-gray-400'}`}>{sub.role}</p>
                    </div>
                    <div className="ml-auto">
                        <ChevronRight size={16} className={selectedUser?.id === sub.id ? 'opacity-100' : 'opacity-0'} />
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-10 text-gray-400 text-xs font-bold uppercase">No members found</div>
              )}
            </div>
          </div>

          {/* RIGHT: Permissions Matrix (8 Columns) */}
          <div className="lg:col-span-8 bg-white border border-gray-200 rounded-[30px] p-0 flex flex-col shadow-sm relative overflow-hidden">
            
            {!selectedUser ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-300 bg-gray-50/30">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                   <Lock size={40} />
                </div>
                <h3 className="text-lg font-black text-gray-900">Permissions Locked</h3>
                <p className="text-sm font-medium mt-2">Select a user from the directory to unlock settings.</p>
              </div>
            ) : (
              <>
                {/* Header for Selected User */}
                <div className="p-6 md:p-8 border-b border-gray-100 flex justify-between items-center bg-white z-10">
                  <div>
                      <h2 className="text-xl font-black text-gray-900 flex items-center gap-2">
                         <ShieldCheck className="text-[#006d5b]" size={20} /> 
                         Access for {selectedUser.name}
                      </h2>
                      <p className="text-gray-400 text-xs font-bold mt-1 uppercase tracking-wide">
                        {Object.keys(perms).filter(k => perms[k]).length} Permissions Active
                      </p>
                  </div>
                  
                  <button 
                    onClick={saveAccess}
                    disabled={loading}
                    className="bg-[#006d5b] hover:bg-[#005a4b] text-white px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest shadow-lg shadow-[#006d5b]/20 flex items-center gap-2 transition-all active:scale-95 disabled:opacity-70"
                  >
                    {loading ? <Loader2 className="animate-spin" size={16}/> : <Save size={16} />} 
                    Save Changes
                  </button>
                </div>

                {/* Scrollable Permissions List */}
                <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-4 bg-gray-50/30">
                  {availableMenus.map((menu) => {
                    // SECURITY CHECK
                    const hasAccessToThisGroup = role === 'SUPER_ADMIN' || role === 'ADMIN' || !!myPermissions[menu.title];
                    if (!hasAccessToThisGroup) return null;

                    const isActive = perms[menu.title];
                    const isExpanded = expandedMenu === menu.title;

                    return (
                      <div key={menu.id} className={`border rounded-2xl overflow-hidden transition-all bg-white ${isActive ? 'border-[#006d5b] shadow-sm' : 'border-gray-100'}`}>
                        {/* Parent Menu Row */}
                        <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => setExpandedMenu(isExpanded ? null : menu.title)}>
                          <div className="flex items-center gap-4">
                            <div 
                              onClick={(e) => { e.stopPropagation(); setPerms({...perms, [menu.title]: !perms[menu.title]}); }}
                              className={`w-6 h-6 rounded-lg flex items-center justify-center border-2 transition-all ${isActive ? 'bg-[#006d5b] border-[#006d5b]' : 'border-gray-300 bg-white'}`}
                            >
                               {isActive && <CheckCircle size={14} className="text-white" />}
                            </div>
                            <span className={`font-black text-sm uppercase tracking-wide ${isActive ? 'text-gray-900' : 'text-gray-500'}`}>{menu.title}</span>
                          </div>
                          
                          <div className={`p-2 text-gray-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                             <ChevronDown size={18} />
                          </div>
                        </div>

                        {/* Sub-Items (Pages) List */}
                        {isExpanded && (
                          <div className="border-t border-gray-100 bg-gray-50/50 p-4 pl-14 grid grid-cols-1 md:grid-cols-2 gap-3 animate-fade-in">
                            {menu.subItems.map((sub: any) => {
                              const canIAssignThisPage = role === 'SUPER_ADMIN' || role === 'ADMIN' || !!myPermissions[sub.path];
                              if (!canIAssignThisPage) return null;

                              const isSubActive = !!perms[sub.path];

                              return (
                                  <label key={sub.path} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${isSubActive ? 'bg-white border-[#006d5b]/30 shadow-sm' : 'border-transparent hover:bg-gray-100'}`}>
                                    <div className={`w-5 h-5 rounded flex items-center justify-center border transition-all ${isSubActive ? 'bg-[#006d5b] border-[#006d5b]' : 'border-gray-300 bg-white'}`}>
                                       {isSubActive && <CheckCircle size={12} className="text-white" />}
                                    </div>
                                    <input 
                                      type="checkbox" 
                                      checked={isSubActive} 
                                      onChange={() => handleSubItemToggle(menu.title, sub.path)}
                                      className="hidden"
                                    />
                                    <span className={`text-xs font-bold uppercase ${isSubActive ? 'text-[#006d5b]' : 'text-gray-500'}`}>{sub.name}</span>
                                  </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default GiveAccess;